import React, { useEffect, useMemo, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { Table, Form, Button, DatePicker, Select, message, Empty, Tooltip } from 'antd'
import { secureApi } from '../../Config/api'
import { verifyToken, fetchDevices, downloadPeopleReport } from '../../utils/helpers'
import { GET } from '../../utils/apis'
import { dateFormate, translations, pageSizeOptions } from '../../utils/constants'
import moment from 'moment'
import { setPeopleCounterData, setPeopleCounterPageNumber } from '../../Redux/actions/peopleCounterActions'
import { FaCaretDown } from 'react-icons/fa'
import { elmecLogo } from '../../assets/index'
import { ArrowDownToLine, Eye, Filter, Users } from 'lucide-react'
import { PeopleCounterDetailDrawer } from '../../Components'

const PeopleCounter = ({ language }) => {
  const dispatch = useDispatch()
  const history = useHistory()
  const user = useSelector(state => state?.authReducer?.user)
  const organizationDetails = useSelector(state => state?.authReducer?.organization)
  const tableData = useSelector(state => state?.peopleCounterReducer?.data)
  const currentPage = useSelector(state => state.peopleCounterReducer?.pageNumber || 1)
  const [form] = Form.useForm()
  const [dateRange, setDateRange] = useState([])
  const [organizationId] = useState(organizationDetails?.id)
  const [latestDate, setLatestDate] = useState(null)
  const [deviceList, setDeviceList] = useState([])
  const [selectedDevices, setSelectedDevices] = useState([])
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [pageSize, setPageSize] = useState(10)
  const [totalRecords, setTotalRecords] = useState(0)
  const [detailDrawerVisible, setDetailDrawerVisible] = useState(false)
  const [detailRecords, setDetailRecords] = useState([])
  const [detailLoading, setDetailLoading] = useState(false)
  const [detailDownloadLoading, setDetailDownloadLoading] = useState(false)
  const [detailPeriodLabel, setDetailPeriodLabel] = useState(null)
  const { RangePicker } = DatePicker
  const elmecLogoFullPath = `${window?.location?.origin}${elmecLogo}`;

  useEffect(() => {
    verifyToken(history, dispatch)

    fetchDevices({
      setDevicesData: () => { },
      setTotalDevices: () => { },
      dispatch,
      setDevices: (devices) => {
        setDeviceList(devices || [])
      },
      user,
      organizationId
    })

    fetchLatestData()
  }, [])

  const fetchLatestData = async (customRange = null, customDevices = [], page = currentPage, limit = pageSize) => {
    try {
      const userFiltered = (customRange || customDevices.length > 0) ? 'true' : 'false'

      const [startDate, endDate] = customRange || [
        moment().subtract(30, 'days').format(dateFormate),
        moment().format(dateFormate)
      ]

      const response = await secureApi.get(GET?.LATEST_PEOPLE_COUNTER_DATA, {
        params: {
          startDate,
          endDate,
          organizationId,
          userId: user?.id,
          deviceIds: customDevices.join(','),
          userFiltered,
          page,
          limit
        }
      })

      // console.log(response, 'response')

      if (response?.success) {
        dispatch(setPeopleCounterData(response.groupedData || []))
        setTotalRecords(response?.groupedMeta?.total || 0)
        setLatestDate(response?.groupedMeta?.latestDate || null)
      } else {
        dispatch(setPeopleCounterData([]))
        setTotalRecords(0)
        setLatestDate(null)
      }
    } catch (error) {
      message.error('Error fetching people counter data:', error)
    }
  }

  const onApplyFilters = async () => {
    try {
      const values = form.getFieldsValue()
      const { dateRange, device } = values
      const formattedDateRange = dateRange?.map(date => date.format(dateFormate)) || []
      const selectedDeviceList = device || []
      setSelectedDevices(selectedDeviceList)
      setDateRange(dateRange || [])
      dispatch(setPeopleCounterPageNumber(1))
      fetchLatestData(formattedDateRange, selectedDeviceList, 1, pageSize)
    } catch (error) {
      // console.error('Validation error:', error)
    }
  }

  const onClearFilters = () => {
    form.resetFields()
    setDateRange([])
    setSelectedDevices([])
    setLatestDate(null)
    dispatch(setPeopleCounterData([]))
    dispatch(setPeopleCounterPageNumber(1))
    fetchLatestData(null, [], 1, pageSize)
  }

  const data = useMemo(() => {
    return (tableData || [])?.map((record, index) => ({
      ...record,
      key: `${record?.mac_address || record?.id}-${index}`,
      created_date: record?.created_date || '-',
      created_time: record?.created_time || '-',
      organization_name: record?.organization_name || '-',
      // device_name: record?.device_name || '-',
      enter: record?.enter ?? 0,
      exit: record?.exit ?? 0,
      remaining: record?.remaining ?? 0
    }))
  }, [tableData])

  // Fetches the raw (per-time) detail rows used by both the PDF download and the
  // in-app detail view, normalised so every row has the fields the report needs.
  const fetchDetailRecords = async ({ startDate, endDate, deviceIds = [], userFiltered, limit = 100000 }) => {
    const response = await secureApi.get(GET?.LATEST_PEOPLE_COUNTER_DATA, {
      params: {
        startDate,
        endDate,
        organizationId,
        userId: user?.id,
        deviceIds: deviceIds.join(','),
        userFiltered,
        page: 1,
        limit
      }
    })

    if (!response?.success) return []

    return (response?.data || []).map((rec, index) => ({
      ...rec,
      key: `${rec?.mac_address || rec?.id}-${index}`,
      created_date: rec?.created_date || '-',
      created_time: rec?.created_time || '-',
      organization_name: rec?.organization_name || '-',
      mac_address: rec?.mac_address || '-',
      vehicle_name: rec?.vehicle_name || '-',
      enter: rec?.enter ?? 0,
      exit: rec?.exit ?? 0,
      remaining: rec?.remaining ?? 0
    }))
  }

  // Query params for the currently applied filters (whole table).
  const getFilteredQuery = () => {
    const [startDate, endDate] = dateRange?.length ? dateRange.map(date => date.format(dateFormate)) : [null, null]
    const userFiltered = (dateRange?.length || selectedDevices.length > 0) ? 'true' : 'false'
    return { startDate, endDate, deviceIds: selectedDevices, userFiltered, limit: 100000 }
  }

  // Query params for a single table row (one device on one day).
  const getSingleRowQuery = (record) => ({
    startDate: record?.created_date,
    endDate: record?.created_date,
    deviceIds: record?.mac_address ? [record.mac_address] : [],
    userFiltered: 'true',
    limit: 10000
  })

  const downloadRecords = async (records) => {
    await downloadPeopleReport({
      records,
      elmecLogoImage: organizationDetails?.logo || elmecLogoFullPath,
      isSuperAdmin: user?.role === 'superAdmin',
      language
    })
  }

  const handleDownloadReport = async () => {
    try {
      setDownloadLoading(true);
      const allData = await fetchDetailRecords(getFilteredQuery())

      if (!allData?.length) {
        message.warning(translations[language]?.noDataAvailable || 'No data available');
        return;
      }

      await downloadRecords(allData)
      message.success('Report downloaded')
    } catch (error) {
      message.error('Download failed')
    } finally {
      setDownloadLoading(false);
    }
  }

  const handleSingleDownloadReport = async (record) => {
    try {
      const allData = await fetchDetailRecords(getSingleRowQuery(record))
      if (!allData?.length) {
        message.warning(translations[language]?.noDataAvailable || 'No data available');
        return;
      }
      message.success(translations[language]?.pdfDownloadSuccess)
      downloadRecords(allData)
    } catch (error) {
      message.error('Error downloading single report:', error)
    }
  }

  const openDetailDrawer = async (query, label) => {
    setDetailPeriodLabel(label)
    setDetailRecords([])
    setDetailDrawerVisible(true)
    setDetailLoading(true)
    try {
      const allData = await fetchDetailRecords(query)
      setDetailRecords(allData)
    } catch (error) {
      message.error('Error fetching people counter data:', error)
    } finally {
      setDetailLoading(false)
    }
  }

  // "View Detailed Report" (header): every detail row for the current filters.
  const handleViewReport = () => openDetailDrawer(getFilteredQuery(), periodLabel)

  // "View" (table row): detail rows for that device on that day.
  const handleSingleViewReport = (record) => openDetailDrawer(getSingleRowQuery(record), record?.created_date || null)

  const closeDetailDrawer = () => {
    setDetailDrawerVisible(false)
    setDetailRecords([])
    setDetailPeriodLabel(null)
  }

  const handleDrawerDownload = async () => {
    if (!detailRecords?.length) return
    try {
      setDetailDownloadLoading(true)
      await downloadRecords(detailRecords)
      message.success(translations[language]?.pdfDownloadSuccess)
    } catch (error) {
      message.error('Download failed')
    } finally {
      setDetailDownloadLoading(false)
    }
  }


  const columns = [
    {
      title: translations[language]?.dateTxtReport || 'Date',
      dataIndex: 'created_date',
      key: 'created_date',
    },
    ...(user?.role === 'superAdmin' ? [{
      title: translations[language]?.organizations,
      dataIndex: 'organization_name',
      key: 'organization_name',
      render: (text) => <span className='capitalized-name'>{text || '-'}</span>,
    }] : []),
    {
      title: translations[language]?.vehicleName,
      dataIndex: 'vehicle_name',
      key: 'vehicle_name',
      render: (text) => <span className='rp-vehicle capitalized-name'>{text || '-'}</span>,
    },
    {
      title: translations[language]?.macAddress,
      dataIndex: 'mac_address',
      key: 'mac_address',
      render: (text) => (text ? <span className='vl-plate'>{text}</span> : <span className='vl-muted'>-</span>),
    },
    {
      title: translations[language]?.txtEnterPassengers,
      dataIndex: 'enter',
      key: 'enter',
      align: 'center',
      render: (text) => <span className='rp-num rp-num-enter'>{text ?? 0}</span>,
    },
    {
      title: translations[language]?.txtExitPassengers,
      dataIndex: 'exit',
      key: 'exit',
      align: 'center',
      render: (text) => <span className='rp-num rp-num-exit'>{text ?? 0}</span>,
    },
    {
      title: translations[language]?.txtTotalPassengers,
      dataIndex: 'remaining',
      key: 'remaining',
      align: 'center',
      render: (text) => <span className='rp-num rp-num-total'>{text ?? 0}</span>,
    },
    {
      title: translations[language]?.action,
      key: 'actions',
      align: 'center',
      width: 130,
      render: (_, record) => (
        <div className='vl-actions' style={{ justifyContent: 'center' }}>
          <Tooltip title={translations[language]?.viewReport}>
            <button
              type='button'
              className='vl-action-btn vl-action-view'
              onClick={() => handleSingleViewReport(record)}
            >
              <Eye size={15} />
            </button>
          </Tooltip>
          <Tooltip title={translations[language]?.downloadReport}>
            <button
              type='button'
              className='vl-action-btn vl-action-edit'
              onClick={() => handleSingleDownloadReport(record)}
            >
              <ArrowDownToLine size={15} />
            </button>
          </Tooltip>
        </div>
      )
    },
  ]

  const periodLabel = dateRange?.length === 2 && dateRange[0] && dateRange[1]
    ? `${moment(dateRange[0]).format(dateFormate)} - ${moment(dateRange[1]).format(dateFormate)}`
    : null


  return (
    <div className='main-container'>
      <div className='main-inner-container'>
        <div className='insider-container rp-page'>
          <div className='insider-container-buttons people-counter-header rp-header'>
            <div>
              <h1 className='main-heading2 margin-bottom-0'>{translations[language]?.peopleCountertxt}</h1>
              <p className='rp-header-sub'>{translations[language]?.peopleCounterSubtitle}</p>
            </div>
            <div className='get-reports-container rp-header-actions'>
              <Button className='rp-view-btn' onClick={handleViewReport}>
                {translations[language]?.viewDetailReport}
                <Eye style={{ height: '18px' }} />
              </Button>
              <Button type='primary' className='add-button'
                onClick={handleDownloadReport}
                loading={downloadLoading}
              >
                {translations[language]?.downloadDetailReport}
                <ArrowDownToLine style={{ height: '18px' }} className='icons' />
              </Button>
            </div>
          </div>

          <div className='vd-card rp-filters'>
            <div className='vd-card-header'>
              <h2 className='vd-card-title'>
                <Filter size={16} className='rp-title-icon' />
                {translations[language]?.filtersLabel}
              </h2>
              <div className='rp-filter-actions'>
                <Button className='report-clear-btn' onClick={onClearFilters}>
                  {translations[language]?.clearText}
                </Button>
                <Button className='report-apply-btn' type='primary' onClick={onApplyFilters}>
                  {translations[language]?.applyTxt}
                </Button>
              </div>
            </div>
            <div className='vd-card-body'>
            <Form form={form} layout='vertical' className='filter-form rp-filter-form'>
              <Form.Item
                className='filter-input'
                name='dateRange'
                label={translations[language]?.dateRange}
              >
                <RangePicker
                  format={dateFormate}
                  disabledDate={(current) => current && current > moment().endOf('day')}
                  value={dateRange?.length ? dateRange : null}
                  onChange={(dates) => {
                    setDateRange(dates)
                    form.setFieldsValue({ dateRange: dates })
                  }}
                  placeholder={[
                    translations[language]?.startDate,
                    translations[language]?.endDate
                  ]}
                  className='ip-address-date-picker'
                />
              </Form.Item>

              {/* <Form.Item
                className='filter-input'
                name='device'
                label={translations[language]?.deviceName || 'Device'}
              >
                <Select
                  className='report-select-dropdown'
                  mode='multiple'
                  placeholder={translations[language]?.enterDevice || 'Enter Device Name'}
                  options={deviceList.map(d => ({
                    label: d.device_name,
                    value: d.mac_address
                  }))}
                  filterOption={(input, option) =>
                    option?.label?.toLowerCase()?.includes(input.toLowerCase())
                  }
                />
              </Form.Item> */}

              <Form.Item
                className='filter-input'
                name='device'
                label={translations[language]?.macAddress || 'MAC Address'}
              >
                <Select
                  className='report-select-dropdown mac-address-people-counter'
                  mode='multiple'
                  style={{ display: 'flex', width: '100%' }}
                  placeholder={translations[language]?.enterMacAddress || 'Enter MAC Address'}
                  // options={deviceList.map(d => ({
                  //   label: d.mac_address,
                  //   value: d.mac_address
                  // }))}

                  options={deviceList
                    .filter(d => d.mac_address && d.mac_address.trim() !== '')
                    .map(d => ({
                      label: d.mac_address,
                      value: d.mac_address
                    }))
                  }

                  filterOption={(input, option) =>
                    option?.value?.toLowerCase()?.includes(input.toLowerCase())
                  }
                />
              </Form.Item>

            </Form>
            </div>
          </div>

          <div className='vd-card rp-table-card'>
            <div className='vd-card-header'>
              <div className='vl-list-title'>
                <h2 className='vd-card-title'>
                  <Users size={16} className='rp-title-icon' />
                  {translations[language]?.peopleCounterRecordsTitle}
                </h2>
                <span className='vl-total-chip'>
                  {translations[language]?.totalRecord}: <strong>{totalRecords || 0}</strong>
                </span>
              </div>
              {periodLabel && <span className='vd-card-meta'>{periodLabel}</span>}
            </div>
            <div className='vd-card-body'>
              <Table
                className='vl-table'
                rowKey={(r, i) => `${r?.created_date}-${r?.mac_address}-${i}`}
                dataSource={data}
                columns={columns}
                locale={{
                  emptyText: (
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description={
                        <div className='rp-empty'>
                          <p className='rp-empty-title'>{translations[language]?.noReportData}</p>
                          <p className='rp-empty-hint'>{translations[language]?.noReportDataHint}</p>
                        </div>
                      }
                    />
                  ),
                }}
                pagination={{
                  current: currentPage,
                  pageSize: pageSize,
                  total: totalRecords,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  pageSizeOptions,
                  onChange: (page, pageSize) => {
                    dispatch(setPeopleCounterPageNumber(page))
                    setPageSize(pageSize)
                    fetchLatestData(
                      dateRange?.map(d => d.format(dateFormate)),
                      selectedDevices,
                      page,
                      pageSize
                    )
                  },
                  onShowSizeChange: (current, size) => {
                    setPageSize(size)
                    dispatch(setPeopleCounterPageNumber(1))
                    fetchLatestData(
                      dateRange?.map(d => d.format(dateFormate)),
                      selectedDevices,
                      1,
                      size
                    )
                  }
                }}
                scroll={{ x: 'max-content' }}
              />

            </div>
          </div>

          <PeopleCounterDetailDrawer
            isVisible={detailDrawerVisible}
            onClose={closeDetailDrawer}
            language={language}
            records={detailRecords}
            loading={detailLoading}
            isSuperAdmin={user?.role === 'superAdmin'}
            periodLabel={detailPeriodLabel}
            onDownload={handleDrawerDownload}
            downloadLoading={detailDownloadLoading}
          />

        </div>
      </div>
    </div>
  )
}

export default PeopleCounter